import { DialogPositioningBehavior, DialogType } from '@tableau/widgets';
import * as React from 'react';
import * as _ from 'underscore';
import { PermissionsDialog, PermissionsDialogProps } from '../../app/permissions/PermissionsDialog';
import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { FeatureFlags } from '../FeatureFlags';
import { IActionContext } from '../helpers/ActionContext';
import { PermissionsEditabilityLogicFactory } from '../helpers/permissions/PermissionsEditabilityLogicFactory';
import { L10n } from '../L10n/L10n';
import {
  DatabaseContentType,
  DataRoleContentType,
  DatasourceContentType,
  FlowContentType,
  ProjectContentType,
  TableContentType,
  ViewContentType,
  WorkbookContentType,
} from '../models/ContentTypes';
import { IDataSourceInfo } from '../models/IDataSourceInfo';
import { getPermissionsPanelColumnWidths } from '../models/PermissionsPanelColumnWidths';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { ExplicitPermissions } from '../resources/ExplicitPermissions';
import { Projects } from '../resources/Projects';
import { IGetActionableObjectsResult } from '../ServerApi/requests/GetActionableObjectsRequest';
import { INamedItem } from '../ServerApi/types/resourceParts';
import { ServerService } from '../services/ServerService';
import { ErrorState } from './ErrorState';
import { GetActionableObjectsForAction } from './GetActionableObjectsForAction';
import { LongRunningActionService } from './LongRunningActionService';
import { ModalService, ModalSize } from './ModalService';
import { ReactModalService } from './ReactModalService';
import { SearchSuggestionsService } from './SearchSuggestionsService';
import { ToasterService } from './ToasterService';

export interface IPermissionsDialogScope extends ng.IScope {
  authorizables: Set<any>;
  authorizableType: string;
  permissionsContainerWidth: number;
  permissionsPanelHeight: number;
  actionableItems: Set<INamedItem>;
  templateType: string;
  permissionsDialogProps: PermissionsDialogProps;
}

export class EditPermissionsAction {

  public static $inject = [
    'ModalService',
    '$q',
    '$window',
    '$translate',
    'GetActionableObjectsForAction',
    'ServerService',
    'ExplicitPermissions',
    'PermissionsEditabilityLogicFactory',
    'ErrorState',
    'Projects',
    'ToasterService',
    'LongRunningActionService',
    'SearchSuggestionsService',
  ];

  private static cannotViewAllSelectedMessageIdMap = (() => {
    const map: _.Dictionary<string> = {};
    map[DatabaseContentType.name] = 'permissions_cannotViewSelectedDatabases';
    map[DatasourceContentType.name] = 'permissions_cannotViewSelectedDataSources';
    map[FlowContentType.name] = 'permissions_cannotViewSelectedFlows';
    map[DataRoleContentType.name] = 'permissions_cannotViewSelectedDataRoles';
    map[ProjectContentType.name] = 'permissions_cannotViewSelectedProjects';
    map[TableContentType.name] = 'permissions_cannotViewSelectedTables';
    map[ViewContentType.name] = 'permissions_cannotViewSelectedViews';
    map[WorkbookContentType.name] = 'permissions_cannotViewSelectedWorkbooks';
    return map;
  })();

  constructor(
    private modalService: ModalService,
    private $q: ng.IQService,
    private $window: ng.IWindowService,
    private $translate: ng.translate.ITranslateService,
    private getActionableObjectsForAction: GetActionableObjectsForAction,
    private server: ServerService,
    private explicitPermissions: ExplicitPermissions,
    private permissionsEditabilityLogicFactory: PermissionsEditabilityLogicFactory,
    private errorState: ErrorState,
    private projectsResource: Projects,
    private toasterService: ToasterService,
    private longRunningActionService: LongRunningActionService,
    private searchSuggestionsService: SearchSuggestionsService,
  ) { }

  public showEditPermissionsDialog(items: Set<any>, contentType: string, actionContext: IActionContext): ng.IPromise<any> {

    const scope = <IPermissionsDialogScope> actionContext.scope.$new(true);

    if (contentType === DatasourceContentType.name && this.areAnyEmbeddedDatasourcesSelected(items)) {
      return this.$q.when(this.alertCantShowPermissions({
        translatedMessage: this.$translate.instant(
          'permissions_notShowingEmbeddedAndPublishedDataSources', { ITEM_COUNT: items.size() }, L10n.Interpolations.Count)
      }));
    }

    return this.getActionableObjectsForAction.editPermissions({
      type: contentType,
      ids: items.keys()
    }).then((result: IGetActionableObjectsResult) => {
      const ids = result.ids;
      const unactionableItems = items.clone();
      let actionableItems = new Set<INamedItem>((item: INamedItem) => item.id);
      _.each(ids, (id: string) => {
        const item = items.get(id);
        actionableItems.add(item);
        unactionableItems.remove(item);
      });

      // if there are any items in the section that the user isn't allowed to set permissions on,
      // we don't show the permissions panel
      if (unactionableItems.size() > 0) {
        return this.alertCantShowPermissions({
          translatedMessage: this.getCannotViewAllSelectedMessage(contentType, items)
        }).result;
      }

      const authorizables = actionableItems.clone();
      const authorizableType = contentType;
      const templateType = contentType;

      const flowsEnabled = this.server.flowsOnSiteEnabled();
      const dataRolesEnabled = FeatureFlags.nlsemanticsServiceCustomDataRoleEnabled;
      const widthLayout = getPermissionsPanelColumnWidths(templateType, flowsEnabled, dataRolesEnabled);
      const permissionsContainerWidth = widthLayout.getPermissionsDialogWidth($(this.$window).width(), flowsEnabled, dataRolesEnabled);

      const permissionsPanelHeight = this.calculatePermissionsPanelHeight();

      if (FeatureFlags.permissionsDialogReact) {
        // TODO: this is just what information is sent to the angular dialog.
        // Will be updated with actually needed props once dialog contents are
        // implemented and what is needed is known.
        scope.permissionsDialogProps = {
          authorizables: authorizables,
          authorizableType: authorizableType,
          permissionsContainerWidth: permissionsContainerWidth,
          permissionsPanelHeight: permissionsPanelHeight,
          explicitPermissions: this.explicitPermissions,
          permissionsEditabilityLogicFactory: this.permissionsEditabilityLogicFactory,
          errorState: this.errorState,
          projectsResource: this.projectsResource,
          toasterService: this.toasterService,
          longRunningActionService: this.longRunningActionService,
          searchSuggestionsService: this.searchSuggestionsService,
        };
      } else {
        scope.authorizables = authorizables;
        scope.authorizableType = authorizableType;
        scope.permissionsContainerWidth = permissionsContainerWidth;
        scope.permissionsPanelHeight = permissionsPanelHeight;
        scope.actionableItems = actionableItems;
        scope.templateType = templateType;
      }
      return this.openPermissionsDialog(scope).result;
    }).finally(() => scope.$destroy());
  }

  public openPermissionsDialog(scope: IPermissionsDialogScope) {
    if (FeatureFlags.permissionsDialogReact) {
      return ReactModalService.open({
        testId: 'permissions-dialog-react',
        type: DialogType.LightweightTitleBar,
        dialogWidth: scope.permissionsDialogProps.permissionsContainerWidth,
        dialogContent: React.createElement(PermissionsDialog, scope.permissionsDialogProps),
        dialogBodyStyle: { marginTop: CommonStyles.Sizes.LargeSpace, position: 'static' },
        showCloseButton: true,
        positioningBehavior: DialogPositioningBehavior.Maximized,
        shouldReturnFocusOnClose: true,
        title: Messages.permissions(),
      });
    } else {
      return this.modalService.open({
        scope: scope,
        templateUrl: 'permissionsDialog.html',
        size: ModalSize.fitToContent,
      });
    }
  }

  private alertCantShowPermissions(message: { messageId?: string; translatedMessage?: string }) {
    return this.modalService.alert(_.extend({
      titleId: 'permissions_cannotViewPermissions'
    }, message));
  }

  /**
   * Calculates a height to make the permissions panel fit in smaller screen sizes.
   * TODO: refactor the layout of the permissions panel so this isn't needed.
   */
  private calculatePermissionsPanelHeight(): number {
    const modalDialog = 24 + 18 + 2; // top padding, bottom padding, border (.tb-modal)
    const modalDialogTitle = 21 + 18; // height, bottom margin (.tb-dialog-title)

    const availableHeight = $(this.$window).height();
    return availableHeight - (modalDialog + modalDialogTitle + ModalService.MinimumSpaceBetweenDialogAndViewport);
  }

  private getCannotViewAllSelectedMessage(authorizableType: string, unactionableItems: Set<INamedItem>): string {
    return this.$translate.instant(
      EditPermissionsAction.cannotViewAllSelectedMessageIdMap[authorizableType],
      {
        ITEM_NAME: L10n.elideIfLong(unactionableItems.first().name),
        ITEM_COUNT: unactionableItems.size()
      },
      L10n.Interpolations.Count);
  }

  private areAnyEmbeddedDatasourcesSelected(datasources: Set<INamedItem>): boolean {
    return !!datasources.find((datasource: IDataSourceInfo) => !!datasource.workbook);
  }

}

Module.instance().service('EditPermissionsAction', EditPermissionsAction);
